import { Alert, Button } from '@themenu/design-system'
import type { FC } from 'react'
import { useIntl } from 'react-intl'

import type { ContactCareReason } from '../../../types/brActivation'
import { environmentVariables } from '../../../utils/environmentVariables'
import { ActivationStepLayout } from './ActivationStepLayout'

/**
 * Terminal contact-care screen. Since 2026-06-12 (contract §7) it is reached ONLY by the
 * user-initiated "PIN forgotten" link (reason `pin_forgotten`) — `/pin` failures now show
 * the inline generic `pin_unavailable` red alert instead of routing here. The reason only
 * adjusts the body copy (always generic, never leaks account state); an unknown reason
 * falls back to the generic body. The single action sends the user to the help center.
 */
export type ContactCareStepProps = {
  reason?: ContactCareReason
  showBackButton?: boolean
  onBack?: () => void
}

const KNOWN_REASONS: ContactCareReason[] = ['pin_forgotten']

export const ContactCareStep: FC<ContactCareStepProps> = ({ reason, showBackButton = false, onBack }) => {
  const { formatMessage } = useIntl()

  // Forward-compatible: any unrecognised reason falls back to the generic body.
  const bodyId =
    reason && KNOWN_REASONS.includes(reason)
      ? `br_activation.contact_care.reason.${reason}`
      : 'br_activation.contact_care.reason.generic'

  return (
    <ActivationStepLayout
      titleId="br_activation.contact_care.title"
      testId="br-activation-contact-care"
      showBackButton={showBackButton}
      onBack={onBack}
      footer={
        <Button
          data-testid="br-activation-contact-care-help"
          variant="primary"
          className="w-full"
          component="a"
          href={environmentVariables('BR_HELP_URL')}
        >
          {formatMessage({ id: 'help.faq' })}
        </Button>
      }
    >
      <Alert severity="error" backgroundOpacity="solid" secondaryText={formatMessage({ id: bodyId })} />
    </ActivationStepLayout>
  )
}
